class MagnetoProductForm extends HTMLElement {
  connectedCallback() {
    const productSummary = this.closest(".product-summary") || this;
    this.form = this.querySelector("form");
    this.variantInput = this.querySelector('[name="id"]');
    this.priceNode = productSummary.querySelector("[data-product-price]");
    this.skuNode = productSummary.querySelector("[data-product-sku]");
    this.mediaImage = this.closest(".product-detail")?.querySelector("[data-product-media-image]");
    this.submit = this.querySelector('[type="submit"]');
    this.inquiryLink = this.querySelector("[data-catalog-inquiry]");
    this.personalizationField = this.querySelector("[data-personalization-field]");
    this.variants = JSON.parse(this.querySelector("[data-variants-json]")?.textContent || "[]");
    this.variantMedia = JSON.parse(this.querySelector("[data-variant-media-json]")?.textContent || "{}");
    this.defaultMedia = this.mediaImage
      ? {
          src: this.mediaImage.currentSrc || this.mediaImage.src,
          srcset: this.mediaImage.getAttribute("srcset") || "",
          alt: this.mediaImage.alt || ""
        }
      : null;
    this.moneyFormat = this.dataset.moneyFormat || "€{{amount}}";

    this.addEventListener("change", this.onChange.bind(this));
    this.updateSelectedVariant();
  }

  onChange(event) {
    if (event.target.matches("[data-option-input]")) {
      this.updateSelectedVariant();
    }
  }

  selectedOptions() {
    return [...this.querySelectorAll("[data-option-position]")].map((group) => {
      const checked = group.querySelector("[data-option-input]:checked");
      return checked?.value || group.dataset.optionDefault;
    });
  }

  updateSelectedVariant() {
    const selected = this.selectedOptions();
    const variant = selected.length
      ? this.variants.find((item) => item.options.every((option, index) => option === selected[index]))
      : this.variants.find((item) => item.available) || this.variants[0];

    if (!variant) {
      return;
    }

    this.variantInput.value = variant.id;
    if (this.priceNode) this.priceNode.textContent = this.formatMoney(variant.price);
    if (this.skuNode) this.skuNode.textContent = variant.sku || "";
    this.updateMedia(variant);
    this.updateInquiryLink(variant, selected);
    if (this.submit) {
      this.submit.disabled = !variant.available;
      this.submit.textContent = variant.available ? "Aggiungi al carrello" : "Non disponibile";
    }

    this.togglePersonalization(selected);
  }

  updateMedia(variant) {
    if (!this.mediaImage) return;

    const media = this.variantMedia[String(variant.id)] || this.defaultMedia;
    if (!media?.src) return;

    this.mediaImage.src = media.src;
    if (media.srcset) {
      this.mediaImage.srcset = media.srcset;
    } else {
      this.mediaImage.removeAttribute("srcset");
    }
    if (media.alt) {
      this.mediaImage.alt = media.alt;
    }
  }

  updateInquiryLink(variant, selected) {
    if (!this.inquiryLink) return;

    const url = new URL(this.inquiryLink.dataset.baseUrl || "/pages/contact", window.location.origin);
    url.searchParams.set("prodotto", this.inquiryLink.dataset.productHandle || "");
    const visibleOptions = [...this.querySelectorAll("[data-option-position]:not([data-option-hidden])")]
      .map((group) => group.querySelector("[data-option-input]:checked")?.value)
      .filter(Boolean);
    url.searchParams.delete("variante");
    if (visibleOptions.length) {
      url.searchParams.set("variante", visibleOptions.join(" / "));
    }
    if (variant.sku) url.searchParams.set("sku", variant.sku);

    this.inquiryLink.href = `${url.pathname}${url.search}`;
  }

  togglePersonalization(selected) {
    if (!this.personalizationField) return;
    const chosen = selected.join(" ").toLowerCase();
    const isPersonalized = chosen.includes("incisione") || chosen.includes("quadricromia");
    this.personalizationField.hidden = !isPersonalized;
    const textarea = this.personalizationField.querySelector("textarea");
    if (textarea) {
      textarea.disabled = !isPersonalized;
      textarea.required = isPersonalized;
    }
  }

  formatMoney(cents) {
    const value = new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR"
    }).format(cents / 100);
    return value;
  }
}

customElements.define("magneto-product-form", MagnetoProductForm);

document.addEventListener("click", (event) => {
  const toggle = event.target.closest("[data-category-toggle]");
  if (!toggle) return;

  const group = toggle.closest(".category-product-group");
  const hiddenItems = group?.querySelectorAll("[data-category-extra]");
  if (!group || !hiddenItems?.length) return;

  const isExpanded = toggle.getAttribute("aria-expanded") === "true";
  hiddenItems.forEach((item) => {
    item.hidden = isExpanded;
  });
  toggle.setAttribute("aria-expanded", String(!isExpanded));
  const label = toggle.querySelector("[data-category-toggle-label]");
  if (label) {
    label.textContent = isExpanded ? toggle.dataset.showLabel : toggle.dataset.hideLabel;
  }
  const count = toggle.querySelector("[data-category-toggle-count]");
  if (count) {
    count.hidden = !isExpanded;
  }
});

class HeaderSearch {
  constructor(form) {
    this.form = form;
    this.input = form.querySelector("[data-header-search-input]");
    this.results = form.querySelector("[data-header-search-results]");
    this.abortController = null;
    this.cache = new Map();
    this.currentResults = [];
    this.categories = [
      { type: "category", title: "Auto", url: "/collections/auto", detail: "Vedi tutti i prodotti Auto" },
      { type: "category", title: "Moto", url: "/collections/moto", detail: "Vedi tutti i prodotti Moto" },
      { type: "category", title: "Bici", url: "/collections/bici", detail: "Vedi tutti i prodotti Bici" },
      { type: "category", title: "Scooter", url: "/collections/scooter", detail: "Vedi tutti i prodotti Scooter" },
      { type: "category", title: "Accessori", url: "/collections/accessori", detail: "Vedi tutti gli accessori" }
    ];

    this.form.addEventListener("submit", this.onSubmit.bind(this));
    this.input.addEventListener("input", this.onInput.bind(this));
    this.input.addEventListener("focus", this.onInput.bind(this));
    this.input.addEventListener("keydown", this.onKeyDown.bind(this));
    document.addEventListener("click", this.onDocumentClick.bind(this));
  }

  onSubmit(event) {
    event.preventDefault();
    this.submitSearch(this.input.value.trim());
  }

  onInput() {
    window.clearTimeout(this.inputTimer);
    this.inputTimer = window.setTimeout(() => {
      this.search(this.input.value.trim());
    }, 180);
  }

  onKeyDown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
      this.input.blur();
    }
  }

  onDocumentClick(event) {
    if (!this.form.contains(event.target)) {
      this.close();
    }
  }

  async search(query) {
    this.latestQuery = query;
    if (query.length < 2) {
      this.renderMessage("Scrivi almeno 2 lettere per cercare nel catalogo.");
      return;
    }

    this.renderMessage("Cerco nel catalogo…");
    const categoryResults = this.matchCategories(query);

    try {
      const products = await this.fetchProducts(query);
      if (this.latestQuery !== query) return;
      const results = [...categoryResults, ...products];
      if (!results.length) {
        this.renderMessage("Nessun prodotto trovato. Prova con auto, moto, bici o scooter.");
        return;
      }
      this.renderResults(results);
    } catch {
      const fallbackProducts = this.localProducts(query);
      if (this.latestQuery !== query) return;
      const results = [...categoryResults, ...fallbackProducts];
      if (results.length) {
        this.renderResults(results);
        return;
      }
      this.renderMessage("Ricerca non disponibile in questo momento.");
    }
  }

  async submitSearch(query) {
    if (query.length < 2) {
      this.renderMessage("Scrivi almeno 2 lettere per cercare nel catalogo.");
      return;
    }

    const exactCategory = this.matchCategories(query).find(
      (category) => this.normalizeText(category.title) === this.normalizeText(query)
    );
    if (exactCategory) {
      window.location.href = exactCategory.url;
      return;
    }

    if (!this.currentResults.length || this.input.dataset.currentQuery !== query) {
      await this.search(query);
    }

    const exactResult = this.currentResults.find(
      (result) => this.normalizeText(result.title) === this.normalizeText(query)
    );
    const bestResult = exactResult || this.currentResults[0];

    if (bestResult?.url) {
      window.location.href = bestResult.url;
      return;
    }

    this.renderMessage("Nessun prodotto trovato. Prova con auto, moto, bici o scooter.");
  }

  async fetchProducts(query) {
    if (this.cache.has(query)) return this.cache.get(query);

    this.abortController?.abort();
    this.abortController = new AbortController();

    const params = new URLSearchParams({
      q: query,
      "resources[type]": "product",
      "resources[limit]": "6"
    });
    const response = await fetch(`/search/suggest.json?${params}`, {
      signal: this.abortController.signal,
      headers: { Accept: "application/json" }
    });
    if (!response.ok) throw new Error("Search request failed");

    const payload = await response.json();
    const products = (payload.resources?.results?.products || [])
      .filter((product) => !product.gift_card && this.stripHtml(product.title).toLowerCase() !== "gift card")
      .map((product) => ({
        title: this.stripHtml(product.title),
        url: product.url,
        price: this.formatSearchPrice(product.price),
        image: product.image,
        type: "product"
      }));
    this.cache.set(query, products);
    return products;
  }

  localProducts(query) {
    const normalizedQuery = query.toLowerCase();
    return [...document.querySelectorAll(".product-card")].map((card) => {
      const title = card.querySelector(".product-card__title")?.textContent.trim();
      const url = card.querySelector(".product-card__title")?.getAttribute("href");
      const price = card.querySelector(".price")?.textContent.trim();
      const description = card.querySelector(".product-card__description")?.textContent.trim();
      return { title, url, price, description, type: "product" };
    }).filter((product) =>
      product.title?.toLowerCase() !== "gift card" &&
      [product.title, product.description].filter(Boolean).join(" ").toLowerCase().includes(normalizedQuery)
    ).slice(0, 6);
  }

  matchCategories(query) {
    const normalizedQuery = this.normalizeText(query);
    if (!normalizedQuery) return [];

    return this.categories.filter((category) => {
      const normalizedTitle = this.normalizeText(category.title);
      return normalizedTitle.includes(normalizedQuery) || normalizedQuery.includes(normalizedTitle);
    });
  }

  renderResults(results) {
    this.currentResults = results;
    this.input.dataset.currentQuery = this.input.value.trim();
    this.results.innerHTML = `
      <div class="header-search__list">
        ${results.map((result) => `
          <a class="header-search__item ${result.image ? "" : "header-search__item--text"}" href="${this.escapeHtml(result.url)}">
            ${result.image ? `<img src="${this.escapeHtml(result.image)}" alt="">` : ""}
            <span>
              <strong>${result.type === "category" ? "Categoria: " : ""}${this.escapeHtml(result.title)}</strong>
              ${result.detail || result.price ? `<small>${this.escapeHtml(result.detail || result.price)}</small>` : ""}
            </span>
          </a>
        `).join("")}
      </div>
    `;
    this.open();
  }

  renderMessage(message) {
    this.currentResults = [];
    delete this.input.dataset.currentQuery;
    this.results.innerHTML = `<p class="header-search__message">${message}</p>`;
    this.open();
  }

  open() {
    this.results.hidden = false;
    this.input.setAttribute("aria-expanded", "true");
  }

  close() {
    this.results.hidden = true;
    this.input.setAttribute("aria-expanded", "false");
  }

  stripHtml(value = "") {
    const element = document.createElement("span");
    element.innerHTML = value;
    return element.textContent || element.innerText || "";
  }

  normalizeText(value = "") {
    return value.toLowerCase().trim();
  }

  escapeHtml(value = "") {
    const element = document.createElement("span");
    element.textContent = value;
    return element.innerHTML;
  }

  formatSearchPrice(price) {
    const numericPrice = Number(String(price).replace(",", "."));
    if (Number.isNaN(numericPrice)) return price;

    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR"
    }).format(numericPrice);
  }
}

document.querySelectorAll("[data-header-search]").forEach((form) => {
  new HeaderSearch(form);
});
